import { Button, Modal, useToastContext } from '@payaca/ambrosia';
import { FC, useCallback, useEffect, useMemo, type JSX } from 'react';
import { useDispatch } from 'react-redux';

import EditJobPage from '@/ui/pages/editJobPage/EditJobPage';

import { gqlClient } from '@/api/graphql-client';
import { actions as jobActions } from '@/api/jobs';
import { graphql } from '@/gql';
import { CreateProposalInput } from '@/gql/graphql';
import { useUserHasPermission } from '@/hooks/usePermissions';
import TemplateSelectionControl from '@/ui/components/templateSelectionControl/TemplateSelectionControl';
import LoaderOverlay from '@payaca/components/loaderOverlay/LoaderOverlay';

import { getUrlSearchParam } from '@payaca/helpers/urlHelper';
import { JobPermissions } from '@payaca/permissions/jobs/job.permissions';
import { userHasRequiredPermission } from '@payaca/permissions/permissions.utils';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import { useDeal } from '@payaca/store/hooks/appState';
import { getListedTemplatesPage } from '@payaca/store/templates/templatesActions';
import {
  PublicEntityTemplate,
  PublicHydratedEntityTemplate,
} from '@payaca/types/entity-templates';
import { Job } from '@payaca/types/jobTypesV2';
import { SortBy } from '@payaca/types/listedTemplateTypes';
import { SortDirection } from '@payaca/types/listViewTypes';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import { useSelector } from '../../../api/state';
import { getUserRoles } from '../../../utils/stateAccessors';
import { PermissionGuard } from '../../components/permissionGuard/PermissionGuard';
import AuthenticatedPageWrapper from '../pageWrappers/authenticatedPageWrapper/AuthenticatedPageWrapper';

const CreateProposalMutation = graphql(`
  mutation CreateProposalMutation($input: CreateProposalInput!) {
    createProposal(input: $input) {
      id
    }
  }
`);

type Props = {
  match: any;
  location: any;
};

const NewEditQuotePage: FC<Props> = ({ match }: Props): JSX.Element => {
  const { mutateAsync: mutateCreateProposal } = useMutation({
    mutationFn: (input: CreateProposalInput) => {
      return gqlClient.request(CreateProposalMutation, { input });
    },
  });

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const quoteId = useMemo(() => match?.params?.quoteId, [match]);
  const dealId = useMemo(() => {
    return getUrlSearchParam(location.search, 'dealId');
  }, [location]);
  const [isInitialising, setIsInitialising] = useState(!quoteId);
  const { pushError } = useToastContext();
  const [hasAnyTemplates, setHasAnyTemplates] = useState(false);
  const userRoles = useSelector(getUserRoles);
  const deal = useDeal(dealId ? +dealId : undefined);

  const hasUseProposalTemplatesPermission = useUserHasPermission({
    permissions: [TemplatesPermissions.USE_PROPOSAL_TEMPLATES],
  });

  const goToProposal = useCallback(
    (proposalId: Job['id']) => {
      history.replace({
        pathname: `/quotes/${proposalId}`,
        search: dealId ? `?dealId=${dealId}` : '',
      });
    },
    [dealId, history]
  );

  const raiseErrorFeedback = useCallback(() => {
    pushError(
      <>
        <strong>Something went wrong</strong> Please click the chat button and
        let us know about it.
      </>
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has a missing dependency: 'pushError'
  }, []);

  const createJobWithoutTemplate = useCallback(() => {
    setIsInitialising(true);
    dispatch(
      jobActions.createJob(
        true,
        (err: any, job: any) => {
          setIsInitialising(false);
          if (job) {
            goToProposal(job.id);
          } else {
            raiseErrorFeedback();
          }
        },
        dealId as any
      )
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'goToProposal', and 'dispatch'
  }, [dealId, raiseErrorFeedback]);

  const createJobWithTemplate = useCallback(
    async (template: PublicEntityTemplate<'proposal'>) => {
      if (!dealId) return;

      setIsInitialising(true);
      await mutateCreateProposal({
        projectId: dealId,
        templatePublicId: template.publicId,
      })
        .then((res) => {
          const proposalId = res.createProposal.id;
          goToProposal(+proposalId);
        })
        .catch((err) => {
          setIsInitialising(false);
          raiseErrorFeedback();
        });
    },
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useCallback has missing dependencies: 'goToProposal', and 'mutateCreateProposal'
    [dealId, raiseErrorFeedback]
  );

  const returnToDeal = () => {
    history.push(`/projects/${deal?.publicId}/proposals`);
  };

  useEffect(() => {
    if (quoteId) {
      return;
    }

    if (!hasUseProposalTemplatesPermission) {
      createJobWithoutTemplate();
    }

    dispatch(
      getListedTemplatesPage.request({
        queryParams: {
          pageSize: 1,
          pageNumber: 1,
          entityTypes: ['proposal'],
          sortBy: SortBy.NAME,
          sortDirection: SortDirection.ASCENDING,
        },
        callback: (page) => {
          const hasTemplates = !!page.items.length;
          setHasAnyTemplates(hasTemplates);

          if (!hasTemplates) {
            createJobWithoutTemplate();
          }
        },
      })
    );
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has missing dependencies: 'hasUseProposalTemplatesPermission', and 'dispatch'
  }, [quoteId, createJobWithoutTemplate]);

  const showTemplateSelectionModal = useMemo(() => {
    const cannotCreateCustomJob =
      !userHasRequiredPermission(userRoles, [JobPermissions.ADD_CUSTOM_JOB]) &&
      userHasRequiredPermission(userRoles, [JobPermissions.ADD_JOB]);

    if (!quoteId) {
      return (
        hasUseProposalTemplatesPermission &&
        (hasAnyTemplates || cannotCreateCustomJob)
      );
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useMemo has a missing dependency: 'quoteId'
  }, [hasUseProposalTemplatesPermission, hasAnyTemplates, userRoles]);

  if (showTemplateSelectionModal) {
    return (
      <AuthenticatedPageWrapper>
        <TemplateSelectionModal
          onApplyTemplate={(template) => {
            if (template) {
              void createJobWithTemplate(template);
            } else {
              createJobWithoutTemplate();
            }
          }}
          onClose={returnToDeal}
        />
      </AuthenticatedPageWrapper>
    );
  }

  if (isInitialising) {
    return <LoaderOverlay />;
  }

  return (
    <>
      <EditJobPage
        jobId={+quoteId || 0}
        sendJobToCustomer={(
          id: number,
          payload: any,
          cb: (err: any, response: any) => void
        ) => dispatch(jobActions.sendQuoteToCustomer(id, payload, cb))}
      />
    </>
  );
};

export default NewEditQuotePage;

const TemplateSelectionModal: FC<{
  onApplyTemplate: (
    selectedTemplate?: PublicHydratedEntityTemplate<'proposal'>
  ) => void;
  onClose: () => void;
}> = ({ onApplyTemplate, onClose }) => {
  const [selectedTemplate, setSelectedTemplate] =
    useState<PublicHydratedEntityTemplate<'proposal'>>();

  return (
    <Modal isOpen={true} onClose={onClose} title="Create a Proposal">
      <Modal.Body>
        <TemplateSelectionControl<'proposal'>
          selectedTemplate={selectedTemplate}
          label="Choose a template"
          onChange={setSelectedTemplate}
          allowedEntityTypes={['proposal']}
        />
      </Modal.Body>

      <Modal.Footer>
        <Modal.Footer.Actions>
          <PermissionGuard
            renderIfHasPermissions={[JobPermissions.ADD_CUSTOM_JOB]}
          >
            <Button variant={'outline'} onClick={() => onApplyTemplate()}>
              Start from scratch
            </Button>
          </PermissionGuard>
          <Button
            disabled={!selectedTemplate}
            onClick={() => {
              onApplyTemplate(selectedTemplate);
            }}
          >
            Apply template
          </Button>
        </Modal.Footer.Actions>
      </Modal.Footer>
    </Modal>
  );
};
